import { useState } from 'react';
import { line, DEFAULT_CURRENCY_CODE, Size, formatCurrency, t, toCents, type PurchasePriceAdjustment, type RepricingProposal } from '@bms2/shared';
import { RadioGroup, TextInput } from '../../components/controls';
import type { RefreshedState } from '../../components/composites/AsyncStatePanel';
import modalStyles from '../../components/modals/modal.module.css';
import { PURCHASE_ORDERS_PATH, useApi } from './api';
import styles from './DocumentPage.module.css';

/** One answer to the question: Yes moves the item's purchase price to the line's, with the sales price settled beside it. */
interface PriceAnswer {
  adjust: 'yes' | 'no' | null;
  salesPrice: string;
}

/** The question's state for one issue window: what it asks, what was answered, and the Yes answers the issue carries. */
export interface PurchasePriceQuestionState {
  rows: readonly RepricingProposal[];
  /** 🔒 The read's standing: only a settled read lets the order go, because rows are none while it loads or fails (decision 3141). */
  status: 'loading' | 'failed' | 'settled';
  answer: (itemId: string) => PriceAnswer;
  setAnswer: (itemId: string, next: Partial<PriceAnswer>) => void;
  /** 🔒 Every row is answered before the order goes (decision 3141). */
  unanswered: boolean;
  adjustments: PurchasePriceAdjustment[];
}

const dollars = (amount: number): string => formatCurrency({ amount: toCents(amount), currencyCode: DEFAULT_CURRENCY_CODE });

/**
 * 🔒 The purchase order's question at issue (decision 3141): each Item a line
 * pays more for than its item file says, read when the Send or Mark as Sent
 * window opens. A viewer refused cost is asked nothing, because the server
 * answers no rows to them.
 */
export function usePurchasePriceQuestion(orderId: string, open: boolean, apiBaseUrl = ''): PurchasePriceQuestionState {
  const read = useApi<{ rows: RepricingProposal[] }>(open ? `${PURCHASE_ORDERS_PATH}/${orderId}/purchase-price-rises` : null, apiBaseUrl);
  const kept = read.state.kind === 'ready' ? (read.state as RefreshedState<{ rows: RepricingProposal[] }>) : null;
  const rows = read.state.kind === 'ready' ? read.state.data.rows : [];
  // A closed window reads nothing (`empty`); an open one is loading (a reread under the last answer included), failed (a reread that failed included) or settled.
  const status = read.state.kind === 'failed' || kept?.reloadError !== undefined ? 'failed' : read.state.kind === 'loading' || kept?.refreshing ? 'loading' : 'settled';
  // 🔒 A window that closes forgets what it was answered, the frozen sales-price string with it, so a reopened one asks afresh.
  const [held, setHeld] = useState<{ open: boolean; answers: Record<string, PriceAnswer> }>({ open, answers: {} });
  if (held.open !== open) setHeld({ open, answers: {} });
  const answers = held.open === open ? held.answers : {};
  const setAnswers = (update: (earlier: Record<string, PriceAnswer>) => Record<string, PriceAnswer>) => setHeld((state) => ({ open: state.open, answers: update(state.answers) }));
  // 🔒 The offer is the higher of the proposal and today's sales price, so a Yes left as offered never cuts the price (decision 3141).
  const offered = (row: RepricingProposal | undefined): string => (row ? String(Math.max(row.proposedSalesPrice, row.oldSalesPrice)) : '');
  const answer = (itemId: string): PriceAnswer => answers[itemId] ?? { adjust: null, salesPrice: offered(rows.find((row) => row.itemId === itemId)) };
  const setAnswer = (itemId: string, next: Partial<PriceAnswer>) => setAnswers((earlier) => ({ ...earlier, [itemId]: { ...answer(itemId), ...earlier[itemId], ...next } }));
  return {
    rows,
    status,
    answer,
    setAnswer,
    unanswered: rows.some((row) => answer(row.itemId).adjust === null),
    adjustments: rows.filter((row) => answer(row.itemId).adjust === 'yes').map((row) => ({ itemId: row.itemId, salesPrice: Number(answer(row.itemId).salesPrice) })),
  };
}

/** The question drawn inside the issue window, one block per Item; nothing at all where the order raises none. */
export function PurchasePriceQuestion({ question }: { question: PurchasePriceQuestionState }) {
  if (question.status === 'failed') return <p className={styles.sentence} data-po-price-status="failed">{t('po.priceRise.failed')}</p>;
  if (question.rows.length === 0) return null;
  return (
    <section data-po-price-question={question.rows.length}>
      <h3 className={modalStyles.fieldLabel}>{t('po.priceRise.title')}</h3>
      <p className={styles.sentence}>{t('po.priceRise.intro')}</p>
      {question.rows.map((row) => {
        const answer = question.answer(row.itemId);
        return (
          <div key={row.itemId} data-po-price-rise={row.itemId} data-po-price-answer={answer.adjust ?? 'none'}>
            <p className={styles.sentence}>{line(row.sku, '·', row.name, '·', t('po.priceRise.pays'), dollars(row.landedPurchasePrice), '·', t('po.priceRise.file'), dollars(row.oldPurchasePrice))}</p>
            <RadioGroup
              name={`priceRise-${row.itemId}`}
              legend={t('po.priceRise.question')}
              orientation="horizontal"
              value={answer.adjust ?? ''}
              onChange={(next) => question.setAnswer(row.itemId, { adjust: next === 'yes' ? 'yes' : 'no' })}
              options={[{ value: 'yes', label: t('po.priceRise.yes') }, { value: 'no', label: t('po.priceRise.no') }]}
            />
            {answer.adjust === 'yes' ? (
              <>
                <TextInput size={Size.Small} label={t('po.priceRise.salesPrice')} value={answer.salesPrice} onChange={(value) => question.setAnswer(row.itemId, { salesPrice: value })} name={`priceRiseSales-${row.itemId}`} />
                <p className={styles.sentence}>{line(t('po.priceRise.sellsToday'), dollars(row.oldSalesPrice))}</p>
                <p className={styles.sentence}>{t('po.priceRise.salesHint')}</p>
              </>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
